/*
 * This file is part of the Det Norske Teatret Nettside 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { type ComponentProps } from 'react';

const DEFAULT_OPTIONS: { label: string; value: ChoiceValue }[] = [];
const DEFAULT_ON_CHANGE = () => {};

type ChoiceValue = string | number;

interface ChoicesProps extends Omit<
  ComponentProps<'input'>,
  'id' | 'type' | 'value' | 'checked' | 'onChange'
> {
  id?: string;
  multiple?: boolean;
  description?: string;
  options?: { label: string; value: ChoiceValue }[];
  value?: ChoiceValue | ChoiceValue[];
  onChange?: (event: {
    target: { value: ChoiceValue | ChoiceValue[] };
  }) => void;
}

/**
 * This is the Checkbox component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */
function Choices({
  id = 'choices',
  multiple = false,
  description,
  options = DEFAULT_OPTIONS,
  value: selected,
  onChange = DEFAULT_ON_CHANGE,
  ...props
}: ChoicesProps) {
  function renderDescription() {
    if (!description) return null;

    return (
      <div
        className="description"
        dangerouslySetInnerHTML={{ __html: description }}
      />
    );
  }

  function renderInput(value: ChoiceValue) {
    if (multiple) {
      const checked = ((selected || []) as ChoiceValue[]).indexOf(value) > -1;

      return (
        <input
          id={`${id}-${value}`}
          type="checkbox"
          value={value}
          checked={checked}
          onChange={() => {
            let newValue = selected;

            if (checked) {
              newValue = (selected as ChoiceValue[]).filter(
                current => current !== value
              );
            } else {
              newValue = [...(selected as ChoiceValue[]), value];
            }
            onChange({ target: { value: newValue } });
          }}
          {...props}
        />
      );
    }

    return (
      <input
        id={`${id}-${value}`}
        type="radio"
        value={value}
        checked={selected == value}
        onChange={() => {
          onChange({ target: { value } });
        }}
        {...props}
      />
    );
  }

  return (
    <>
      {renderDescription()}
      <div className="options">
        {options.map(({ label, value }) => {
          return (
            <div key={`${id}-${value}`} className="checkbox">
              {renderInput(value)}
              <label htmlFor={`${id}-${value}`} className="checkbox">
                <span>{label}</span>
              </label>
            </div>
          );
        })}
      </div>
    </>
  );
}

export default Choices;
